import { useState, useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { type CellContext } from "@tanstack/react-table";
import { WaitlistEntries, WaitlistEntryNode, WaitlistEntry, Variables } from "./types";
import { WAITLIST_ENTRIES_QUERY } from "./queries";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import usePagination from "@/hooks/usePagination";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import Table, { TableDetails } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import AvatarDetails from "@/components/AvatarDetails";
import OverrideModal from "./_components/OverrideModal";
import { EventOutletContext } from "../_layout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Avatar from "@/components/Avatar";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { formatPhoneNumber } from "@/lib/phoneHelpers";

export default function WaitlistPage() {
  const { eventSlug = "" } = useParams();
  const { Menu, setMenu } = useTableRowMenu();
  const { setErrorMessage, eventData } = useOutletContext<EventOutletContext>();
  const { isCompactLayout: isMobile } = useIsCompactLayout();

  const { paginationVariables, renderFooter, resetPagination } = usePagination();
  const [variables, setVariables] = useState<Variables>({ eventSlug });
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);

  useEffect(() => {
    resetPagination();
    setVariables({
      eventSlug
    });
  }, [eventSlug, resetPagination]);

  const eventId = eventData?.id;
  const { hasWaitlist } = eventData || {};
  const showOverrideModal = !!(eventId && selectedPersonId);

  const { data, loading: queryLoading } = useQuery<WaitlistEntries>(WAITLIST_ENTRIES_QUERY, {
    variables: { ...variables, ...paginationVariables }
  });

  const waitlistEntriesData: WaitlistEntry[] = (data?.eventWaitlistEntries?.edges || []).map(
    ({ node: { age, createdAt, ...entry } }: WaitlistEntryNode) => ({
      ...entry,
      age,
      createdAt: createdAt ? toDateTimeFormat(createdAt, "LLL dd, yyyy h:mm a (ZZZZ)") : ""
    })
  );

  const handleCsvExport = () => {
    const headers = ["Name", "PGA ID", "Gender", "Age", "Email", "Phone", "Signup Date"];
    const rows = (data?.eventWaitlistEntries?.edges || []).map(({ node }) => [
      node.entryName || "",
      node.pgaId || "",
      node.gender || "",
      node.age?.toString() || "",
      node.email || "",
      node.phone || "",
      node.createdAt ? toDateTimeFormat(node.createdAt, "LLL dd, yyyy h:mm a (ZZZZ)") : ""
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
    link.download = `waitlist-${eventSlug}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const columns = [
    ...(isMobile
      ? [
          {
            cell: (cellProps: CellContext<WaitlistEntry, unknown>) => {
              const { profilePhoto } = cellProps.row.original;
              return <Avatar picture={profilePhoto} />;
            },
            id: "avatar"
          },
          {
            cell: (cellProps: CellContext<WaitlistEntry, unknown>) => {
              const { entryName } = cellProps.row.original;
              return <div className="text-body-medium">{entryName}</div>;
            },
            id: "entryName"
          }
        ]
      : [
          {
            cell: (cellProps: CellContext<WaitlistEntry, unknown>) => {
              const { profilePhoto, entryName } = cellProps.row.original;
              return <AvatarDetails picture={profilePhoto} title={entryName} text="" />;
            },
            id: "name",
            header: "Name",
            size: 500,
            meta: {
              className: getStickyColumnClasses(!!waitlistEntriesData?.length)
            }
          }
        ]),
    { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
    {
      accessorKey: "gender",
      header: "Gender",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    {
      accessorKey: "age",
      header: "Age",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    { accessorKey: "email", header: "Email", enableSorting: false },
    {
      accessorKey: "phone",
      header: "Phone",
      enableSorting: false,
      cell: (cellProps: CellContext<WaitlistEntry, unknown>) => {
        const { phone } = cellProps.row.original;
        return <span>{formatPhoneNumber(phone ?? null)}</span>;
      }
    },
    {
      accessorKey: "createdAt",
      header: "Signup",
      enableSorting: false,
      meta: { headerClassName: "show-header-in-column" }
    },
    {
      cell: (cellProps: CellContext<WaitlistEntry, unknown>) => {
        const { pgaId } = cellProps.row.original;
        const actions: Action[] = [
          {
            text: "Send Registration Override",
            onClick: () => {
              setSelectedPersonId(cellProps.row.original.personId);
            }
          }
        ];

        function onClick(e: React.MouseEvent<HTMLButtonElement>) {
          e.preventDefault();
          e.stopPropagation();
          setMenu({ rowId: cellProps.row.id, actions });
        }

        return (
          <div
            className={isMobile ? "flex flex-col-reverse items-center" : "flex items-center justify-end"}
            data-testid="menu-icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              data-testid="expand-details"
              title="View person details"
              name="open_in_full"
              iconClassName="text-sys-surface-variant"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedPgaId(pgaId);
                setIsPersonSideSheetOpen(true);
              }}
            />
            {actions.length > 0 && (
              <IconButton
                id={actionId(cellProps.row.id)}
                onClick={onClick}
                data-testid="actions"
                title="actions"
                name="more_vert"
                className="!m-0 h-10"
              />
            )}
          </div>
        );
      },
      id: "actions",
      header: () => null,
      size: 100
    }
  ];

  return (
    <>
      {showOverrideModal && (
        <OverrideModal
          eventId={eventId}
          personId={selectedPersonId}
          onClosed={() => {
            setSelectedPersonId(null);
          }}
          onError={(message: string) => {
            setErrorMessage(message);
          }}
        />
      )}
      <div data-testid="waitlist-page">
        <Menu />
        {hasWaitlist ? (
          <Table
            data={waitlistEntriesData}
            loading={queryLoading}
            onClickRow={(row) => {
              setSelectedPgaId(row.original.pgaId);
              setIsPersonSideSheetOpen(true);
            }}
            columns={columns}
            renderDetails={() => (
              <TableDetails
                heading="Event Waitlist"
                body="View all people who have requested notifications about this event should there be an opening"
                actions={
                  waitlistEntriesData.length > 0
                    ? [
                        {
                          label: "Export Current List",
                          variant: "text" as const,
                          icon: "download" as const,
                          onClick: handleCsvExport
                        }
                      ]
                    : []
                }
              />
            )}
            renderFilteredEmptyState={() => (
              <EmptyState
                title="No Waitlist Entries"
                caption="This event does not have any waitlist entries"
                iconName="person"
              />
            )}
            renderFooter={renderFooter(data?.eventWaitlistEntries, { pageCount: waitlistEntriesData.length })}
          />
        ) : (
          <EmptyState
            title="Waitlist Not Enabled"
            caption="This event disables registration when the attendance limit is reached."
            iconName="person"
            className="max-w-[600px]"
          />
        )}
      </div>
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />
    </>
  );
}
